import { queriesPayers } from "@query";
import {
  WSActions,
  WSElement,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import type { insuranceMonitorApiSpec } from "@wingspanhq/wingspan-api-types";
import React, { useMemo } from "react";

import { selectorPayerDisplayName } from "../../../../shared/selectors/selectorPayerDisplayName";
import { InsuranceInstructionsView } from "../../../RequirementDefinitions/routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils/getRequirementDefinitionTemplateByParams";
import { selectorIsAcceptedSourceTypeIncluded } from "../../modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";
import { selectorPolicyTypesFromSettings } from "../../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { ContextItem, RequirementContextType } from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";

type InsuranceCoverageMonitor =
  insuranceMonitorApiSpec.InsuranceCoverageMonitor;

export type RequirementDetailsInstructionsProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction: () => void;
  onCloseAction: () => void;
  customContext?: ContextItem;
  requirement: IRequirementResponse;
  instructions: string;
  monitor?: InsuranceCoverageMonitor;
};

export const RequirementDetailsInstructions: React.FC<
  RequirementDetailsInstructionsProps
> = ({
  requirement,
  contextType,
  isLoading,
  getRequirementDetailsWidgets,
  getRequirementInstructionsWidgets,
  getRequirementInstructions,
  instructions,
  onSuccessAction,
  onCloseAction,
  monitor
}) => {
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: requirement.payerId },
    {
      enabled:
        requirement.requirementType === RequirementType.IdentityVerification,
      retry: false
    }
  );

  const requirementInstructionsWidgets =
    getRequirementInstructionsWidgets(requirement);
  const viewInstructions =
    getRequirementInstructions(requirement)?.(instructions);

  const payerName = useMemo(() => {
    return queryPayer.data
      ? selectorPayerDisplayName(queryPayer.data)
      : "Payer";
  }, [queryPayer.data]);

  const acceptWingspanMasterPolicy = selectorIsAcceptedSourceTypeIncluded(
    "PlanEnrollment",
    monitor?.settings
  );

  const template = getRequirementDefinitionTemplateByParams({
    policyType: selectorPolicyTypesFromSettings(monitor?.settings),
    type: RequirementType.InsuranceCoverage
  });

  return (
    <WSElement shimmer={isLoading}>
      <WSList gap="XL">
        <WSGrid>
          {requirementInstructionsWidgets ? null : (
            <WSGrid.Item span={{ s: "2" }} />
          )}
          <WSGrid.Item span={{ s: "8" }}>
            {requirement.requirementType !==
            RequirementType.InsuranceCoverage ? (
              <>
                <WSText.Paragraph weight="medium">
                  Requirement instructions
                </WSText.Paragraph>
                <WSText.ParagraphSm color="gray500" mt="S">
                  Review the details to ensure you have the necessary
                  information to complete the requirement.
                </WSText.ParagraphSm>

                <WSPanel mt="M" p="L">
                  {viewInstructions}

                  {requirement.requirementType ===
                  RequirementType.IdentityVerification ? (
                    <WSInfoBox mt="M" shimmer={queryPayer.isLoading}>
                      {payerName} takes the threat of employment-related
                      identity theft seriously and requires all contractors to
                      undergo this verification process. {payerName} does not
                      collect, process, store, or access any biometric data
                      collected during the verification process, as defined by
                      applicable data privacy laws.
                    </WSInfoBox>
                  ) : null}
                </WSPanel>
              </>
            ) : (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={requirement.name || template.name}
                policyTypes={selectorPolicyTypesFromSettings(monitor?.settings)}
              />
            )}

            <WSActions
              alignment="fill"
              buttons={[
                {
                  kind: "Primary",
                  label: "Continue",
                  onClick: () => {
                    onSuccessAction();
                  }
                },
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => {
                    onCloseAction();
                  }
                }
              ]}
              mt="XL"
            ></WSActions>
          </WSGrid.Item>
          <WSGrid.Item span={{ s: "4" }}>
            {requirementInstructionsWidgets}
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
